import React, { useState, useEffect } from 'react';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { Button } from "@/components/ui/button";
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuthState } from '@/hooks/AuthContext';
import { Home, ChevronLeft, LayoutDashboard, ChevronDown } from 'lucide-react';
import { AssetsConfig } from '@/services/AssetsConfig';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ProjectHeader = ({ showHomeButton = true, showBackButton = false, collectionData = null, projectName= null }) => {
    const { logout } = useKindeAuth();
    const { user, setAuthState, permissions} = useAuthState();
    const { projectId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
    const [currentProject, setCurrentProject] = useState(null);
    const isAdmin = permissions?.includes('admin');

    const userName = user?.given_name || user?.family_name || user?.email || 'User';
    const userInitials = userName.split(' ').map(n => n[0]).join('').toUpperCase();

    useEffect(() => {
        if (collectionData && projectId) {
            const project = collectionData.projects.find(p => p.id === projectId);
            setCurrentProject(project);
        }
    }, [collectionData, projectId]);

    const handleLogout = async () => {
        try {
            await logout();
            localStorage.removeItem('user');
            setAuthState({
                isAuthenticated: false,
                isLoading: false,
                user: null
            });
            navigate('/welcome');
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    const handleBackToMain = () => {
        navigate(`/project/${projectId}`);
    };

    const handleBackToProjects = () => {
        navigate('/projects');
    };

    const handleProjectChange = (newProjectId) => {
        // Get the current path after the project ID
        const currentPath = location.pathname;
        const baseProjectPath = `/project/${projectId}`;
        const remainingPath = currentPath.replace(baseProjectPath, '');
        
        // Navigate to the new project while maintaining the same path
        navigate(`/project/${newProjectId}${remainingPath}`);
        setIsProjectDropdownOpen(false);
    };

    return (
        <header className="fixed top-0 left-0 right-0 h-14 px-4 flex justify-between items-center bg-gray-100 z-50 shadow-md">
            {/* Left section - Logo */}
            <div className="flex-none">
                <Link 
                    to={AssetsConfig.getLogoUrl().link}
                    target="_blank"
                    rel="noopener noreferrer" 
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                >   
                    <img 
                        src={AssetsConfig.getLogoUrl().logo_url}
                        alt="Logo"
                        // className="ml-2 mt-1 mb-1 h-14 w-auto object-contain"
                        className="my-1 h-12 w-auto aspect-auto object-contain"
                    />
                </Link>
            </div>

            {/* Center section - Collection Navigation */}
            <div className="flex-1 flex justify-center items-center min-w-0">
                {collectionData && currentProject ? (
                    <div className="flex items-center min-w-0 max-w-full">
                        {/* <span className="text-gray-600">{collectionData.name} </span>
                        <span className="text-gray-400">/</span> */}
                        <span className="text-gray-600 hidden sm:inline">{collectionData.name} </span>
                        <span className="text-gray-400 hidden sm:inline">/</span>
                        <DropdownMenu
                            open={isProjectDropdownOpen}
                            onOpenChange={setIsProjectDropdownOpen}
                        >
                            <DropdownMenuTrigger asChild>
                                {/* <Button variant="ghost" className="flex items-center space-x-1">
                                    <span>{currentProject.name}</span>
                                    <ChevronDown className="h-4 w-4" />
                                </Button> */}
                                <Button variant="ghost" className="min-w-0 flex flex-wrap justify-center items-center gap-1 max-w-[220px] sm:max-w-none">
                                    <span className="truncate">{currentProject.name}</span>
                                    <ChevronDown className="h-4 w-4 shrink-0" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-56">
                                {collectionData.projects.map((project) => (
                                    <DropdownMenuItem
                                        key={project.id}
                                        onClick={() => handleProjectChange(project.id)}
                                        className={`${project.id === projectId ? 'bg-blue-50' : ''}`}
                                    >
                                        <span>{project.name}</span>
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                ) : ( projectName && 
                (
                    <div className="flex items-center space-x-2">
                        <span className="text-gray-600">{projectName}</span>
                    </div>            
                ))}
            </div>

            <div className="flex items-center space-x-2">
                {showBackButton && (
                    <Button onClick={handleBackToMain} variant="ghost" size="icon" className="shrink-0">
                        <ChevronLeft className="h-5 w-5" />
                    </Button>
                )}
                {showHomeButton && (
                    <Button onClick={handleBackToProjects} variant="ghost" size="icon" className="shrink-0">
                        <Home className="h-5 w-5" />
                    </Button>
                )}
                <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                            <Avatar className="h-8 w-8 bg-blue-500 text-white">
                                <AvatarImage src={user?.picture} alt={userName} />
                                <AvatarFallback className="text-lg font-bold">{userInitials}</AvatarFallback>
                            </Avatar>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuItem className="flex-col items-start">
                            <div className="font-semibold">{userName}</div>
                            <div className="text-sm text-gray-500">{user?.email}</div>
                        </DropdownMenuItem>

                        {isAdmin && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => navigate('/admin/projects')} className="gap-2">
                                    <LayoutDashboard className="h-4 w-4" />
                                    <span>Admin Dashboard</span>
                                </DropdownMenuItem>
                            </>
                        )}
                        <DropdownMenuSeparator />

                        <DropdownMenuItem onClick={handleLogout}>
                            Logout
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </header>
    );
};

export default ProjectHeader;